import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Send, Check, Radio, MessageSquare, Image as ImageIcon, Award } from "lucide-react";

/**
 * Shared Events Panel — used by School Admin & Facilitator (full control),
 * Educator (schedule + score + media), Parent & Student (read-only broadcast list),
 * Founder (approval queue).
 *
 * mode: "manage" | "execute" | "view" | "approve"
 *   - manage: full CRUD + greetings + push-approval + broadcast (Admin/Facilitator)
 *   - execute: item CRUD + scores + media upload (Educator)
 *   - view:    read-only broadcast list (Parent/Student)
 *   - approve: pending_approval list + approve action (Founder)
 */
export default function EventsPanel({mode="manage"}) {
  const [events, setEvents] = useState([]);
  const [selId, setSelId] = useState("");
  const [f, setF] = useState({name:"", date:"", manager_name:"",
                              guests:[], judges:[], description:"",
                              grade_scope:[], all_school:true});
  const [grades, setGrades] = useState([]);
  const [staff, setStaff] = useState([]);
  const load = async () => {
    try { setEvents((await api.get("/events")).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load events"); }
    if (mode === "manage" || mode === "execute") {
      try { const s = (await api.get("/settings")).data; setGrades(s.grades || []); } catch { /* silent */ }
      try { setStaff((await api.get("/staff")).data); } catch { /* silent — educator may not have access */ }
    }
  };
  useEffect(()=>{ load(); }, []);   // eslint-disable-line
  const selEvent = events.find(e => e.event_id === selId);

  const create = async (e) => { e.preventDefault();
    try {
      const r = await api.post("/events", {...f,
        guests: (f.guests||"").toString().split(",").map(s=>s.trim()).filter(Boolean),
        judges: (f.judges||"").toString().split(",").map(s=>s.trim()).filter(Boolean).map(name => ({name})),
        grade_scope: f.grade_scope || [],
        all_school: !!f.all_school,
      });
      toast.success("Event created");
      setSelId(r.data.event_id);
      setF({name:"", date:"", manager_name:"", guests:[], judges:[], description:"",
             grade_scope:[], all_school:true});
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const push = async (eid) => {
    try { await api.post(`/events/${eid}/push-approval`); toast.success("Pushed for approval"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const approve = async (eid) => {
    try { await api.post(`/events/${eid}/approve`); toast.success("Approved"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const broadcast = async (eid) => {
    try { await api.post(`/events/${eid}/broadcast`); toast.success("Broadcasted!"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const pushMedia = async (eid) => {
    try { const r = await api.post(`/events/${eid}/push-media`); toast.success(`Syndicated ${r.data.count} items`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const del = async (eid) => { if (!window.confirm("Delete event?")) return;
    try { await api.delete(`/events/${eid}`); toast.success("Deleted");
          setSelId(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const filteredEvents = mode === "approve"
    ? events.filter(e => e.status === "pending_approval" || e.status === "approved")
    : events;

  return <div className="space-y-6" data-testid={`events-panel-${mode}`}>
    <div className="grid lg:grid-cols-3 gap-6">
      {(mode === "manage") && (
        <form onSubmit={create} className="nb-card p-6 bg-[#FDE047] space-y-2 h-fit"
              data-testid="event-builder">
          <h3 className="display text-xl font-bold">Event Builder</h3>
          <input className="nb-input" placeholder="Event Name" required
                 value={f.name} onChange={e=>setF({...f, name:e.target.value})}
                 data-testid="event-name"/>
          <input type="date" className="nb-input" required
                 value={f.date} onChange={e=>setF({...f, date:e.target.value})}
                 data-testid="event-date"/>
          <div>
            <label className="flex items-center gap-2 text-xs mb-1">
              <input type="checkbox" checked={!!f.all_school}
                     onChange={e=>setF({...f, all_school:e.target.checked,
                                          grade_scope: e.target.checked ? [] : (f.grade_scope||[])})}
                     data-testid="event-all-school"/>
              Add All School (whole-school event)
            </label>
            {!f.all_school && (
              <div>
                <div className="nb-label">Applicable Grades (multi-select)</div>
                <div className="flex flex-wrap gap-1">
                  {(grades||[]).map(g => {
                    const on = (f.grade_scope||[]).includes(g);
                    return <label key={g}
                            className={`nb-btn px-2 py-0.5 text-[11px] cursor-pointer ${on?"":"nb-btn-ghost"}`}>
                      <input type="checkbox" className="hidden" checked={on}
                             onChange={()=>{
                               const s = new Set(f.grade_scope||[]);
                               on ? s.delete(g) : s.add(g);
                               setF({...f, grade_scope: Array.from(s)});
                             }}
                             data-testid={`event-grade-${g}`}/>{g}
                    </label>;
                  })}
                </div>
              </div>
            )}
          </div>
          <input className="nb-input" placeholder="Event Manager (Name)"
                 value={f.manager_name}
                 onChange={e=>setF({...f, manager_name:e.target.value})}
                 data-testid="event-manager"/>
          <input className="nb-input" placeholder="Guests / Chief Guests (comma-separated)"
                 value={f.guests}
                 onChange={e=>setF({...f, guests:e.target.value})}
                 data-testid="event-guests"/>
          <input className="nb-input" placeholder="Assigned Judges (comma-separated names)"
                 value={f.judges}
                 onChange={e=>setF({...f, judges:e.target.value})}
                 data-testid="event-judges"/>
          <textarea className="nb-textarea" placeholder="Description / Objectives" rows={2}
                    value={f.description}
                    onChange={e=>setF({...f, description:e.target.value})}/>
          <button className="nb-btn w-full justify-center"
                  data-testid="event-create">
            <Plus size={16}/>Create Event</button>
        </form>
      )}

      <div className={mode === "manage" ? "lg:col-span-2 space-y-2" : "lg:col-span-3 space-y-2"}>
        {filteredEvents.length === 0 && (
          <div className="nb-card p-6 bg-white text-center text-slate-500">
            {mode === "view" ? "No events broadcasted yet — stay tuned!" :
             mode === "approve" ? "No events awaiting approval." :
             "No events yet. Create one from the builder ←"}
          </div>
        )}
        {filteredEvents.map(e => {
          const badge = {
            draft: "bg-slate-300", pending_approval: "bg-[#FDBA74]",
            approved: "bg-[#93C5FD]", broadcast: "bg-[#86EFAC]",
          }[e.status] || "bg-slate-200";
          const active = e.event_id === selId;
          return <div key={e.event_id}
                className={`nb-card p-4 ${active?"bg-yellow-50":"bg-white"} cursor-pointer`}
                onClick={()=>setSelId(active ? "" : e.event_id)}
                data-testid={`event-row-${e.event_id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="display font-bold text-lg">{e.name}</span>
              <span className="mono text-sm text-slate-600">{e.date}</span>
              <span className={`nb-badge ${badge}`}>{(e.status||"").replace("_"," ").toUpperCase()}</span>
              <span className="ml-auto text-xs">Items: {(e.items||[]).length} · Media: {(e.media||[]).length}</span>
            </div>
            {e.manager_name && <div className="text-xs mt-1">Manager: <b>{e.manager_name}</b></div>}
            {(e.grade_scope || []).length > 0 && (
              <div className="text-xs mt-1">Grades: {(e.grade_scope||[]).map(g => (
                <span key={g} className="nb-badge bg-[#93C5FD] mr-1">{g}</span>
              ))}</div>
            )}
            {e.all_school && <div className="text-xs mt-1"><span className="nb-badge bg-[#86EFAC]">WHOLE SCHOOL</span></div>}
            {(e.guests||[]).length > 0 && <div className="text-xs">Guests: {e.guests.join(", ")}</div>}
            {active && (
              <div className="mt-3 space-y-3" onClick={ev=>ev.stopPropagation()}>
                {(mode === "manage") && (
                  <div className="flex flex-wrap gap-2">
                    {e.status === "draft" && (
                      <button onClick={()=>push(e.event_id)} className="nb-btn"
                              data-testid={`event-push-${e.event_id}`}>
                        <Send size={14}/> Push for Approval
                      </button>
                    )}
                    {e.status === "approved" && (
                      <button onClick={()=>broadcast(e.event_id)} className="nb-btn"
                              data-testid={`event-broadcast-${e.event_id}`}>
                        <Radio size={14}/> Broadcast to All
                      </button>
                    )}
                    {e.status === "broadcast" && (e.media||[]).length > 0 && (
                      <button onClick={()=>pushMedia(e.event_id)} className="nb-btn"
                              data-testid={`event-push-media-${e.event_id}`}>
                        <ImageIcon size={14}/> Push Media
                      </button>
                    )}
                    <GreetingGenerator eventId={e.event_id}/>
                    <button onClick={()=>del(e.event_id)} className="nb-btn nb-btn-danger">
                      <Trash2 size={14}/>Delete
                    </button>
                  </div>
                )}
                {(mode === "approve") && e.status === "pending_approval" && (
                  <button onClick={()=>approve(e.event_id)} className="nb-btn nb-btn-secondary"
                          data-testid={`event-approve-${e.event_id}`}>
                    <Check size={14}/>Approve Event
                  </button>
                )}
                {(mode === "manage" || mode === "execute" || mode === "view") && (
                  <ScheduleTable event={e} staff={staff} readonly={mode === "view"} mode={mode} onDone={load}/>
                )}
                {(mode === "execute" || mode === "manage") && (
                  <MediaCapture event={e} onDone={load}/>
                )}
                {mode === "view" && (e.media||[]).length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {e.media.map(m => <MediaThumb key={m.media_id} m={m}/>)}
                  </div>
                )}
              </div>
            )}
          </div>;
        })}
      </div>
    </div>
  </div>;
}

/* ---- Schedule of Events execution table (11 columns) ---- */
function ScheduleTable({event, staff, readonly, mode, onDone}) {
  // Iter30 — Educators (execute mode) cannot add/delete items or change
  // structural fields (program name, time, staff assignment, rolls, approval).
  // They retain execution permissions: rehearsal toggle, scores, rank, cert type.
  const execMode = mode === "execute";
  const structuralReadonly = readonly || execMode;
  const [items, setItems] = useState(event.items || []);
  const [newItem, setNewItem] = useState({program_name:"", time_from:"", time_to:"",
                                          responsible_staff_id:"", responsible_staff_name:"",
                                          team_rolls:"", team_members:[]});
  useEffect(()=>{ setItems(event.items || []); }, [event]);
  const CERT_TYPES = ["participation","appreciation","merit"];
  const resolveRolls = async (rollsStr) => {
    const rolls = rollsStr.split(",").map(x=>x.trim()).filter(Boolean);
    if (rolls.length === 0) return [];
    try {
      const r = await api.post("/students/resolve-rolls", {rolls});
      return r.data.map(s => `${s.roll_no}·${s.name}`);
    } catch { return rolls; }
  };
  const add = async () => {
    if (!newItem.program_name.trim()) return toast.error("Program name required");
    const resolved = await resolveRolls(newItem.team_rolls || "");
    const staffId = newItem.responsible_staff_id;
    const staffName = staffId ? (staff.find(s => s.staff_id === staffId)?.name || "") : newItem.responsible_staff_name;
    try {
      await api.post(`/events/${event.event_id}/items`, {
        program_name: newItem.program_name,
        time_from: newItem.time_from, time_to: newItem.time_to,
        responsible_staff_id: staffId || null,
        responsible_staff_name: staffName,
        team_members: resolved,
      });
      setNewItem({program_name:"", time_from:"", time_to:"",
                   responsible_staff_id:"", responsible_staff_name:"",
                   team_rolls:"", team_members:[]});
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const patch = async (itemId, changes) => {
    try {
      await api.patch(`/events/${event.event_id}/items/${itemId}`, changes);
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const patchStaff = async (itemId, staffId) => {
    const s = staff.find(x => x.staff_id === staffId);
    await patch(itemId, {responsible_staff_id: staffId || null,
                          responsible_staff_name: s?.name || ""});
  };
  const patchRolls = async (itemId, rollsStr) => {
    const resolved = await resolveRolls(rollsStr);
    await patch(itemId, {team_members: resolved});
  };
  const del = async (itemId) => {
    if (!window.confirm("Delete item?")) return;
    try { await api.delete(`/events/${event.event_id}/items/${itemId}`); onDone && onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return <div className="nb-card p-0 overflow-x-auto" data-testid={`event-schedule-${event.event_id}`}>
    <table className="nb-table text-xs" style={{minWidth: 1240}}>
      <thead><tr>
        <th style={{minWidth:40}}>Sl</th>
        <th style={{minWidth:160}}>Program</th>
        <th style={{minWidth:150}}>Time</th>
        <th style={{minWidth:170}}>Responsible Staff</th>
        <th style={{minWidth:220}}>Team / Rolls</th>
        <th style={{minWidth:80}}>Rehearsed</th>
        <th style={{minWidth:110}}>Approval</th>
        <th style={{minWidth:70}}>Score</th>
        <th style={{minWidth:70}}>Rank</th>
        <th style={{minWidth:130}}>Certificate</th>
        <th style={{minWidth:150}}>Actions</th>
      </tr></thead>
      <tbody>
        {items.map(it => (
          <tr key={it.item_id} data-testid={`event-item-${it.item_id}`}>
            <td className="mono">{it.sl_no}</td>
            <td className="font-bold">{it.program_name}</td>
            <td className="mono whitespace-nowrap">{(it.time_from||"—")} - {(it.time_to||"—")}</td>
            <td>
              {structuralReadonly ? (it.responsible_staff_name || "—") :
                <select className="nb-select text-xs w-full"
                        value={it.responsible_staff_id || ""}
                        onChange={e=>patchStaff(it.item_id, e.target.value)}
                        data-testid={`event-item-staff-${it.item_id}`}>
                  <option value="">— Assign Staff —</option>
                  {(staff||[]).map(s => <option key={s.staff_id} value={s.staff_id}>{s.name} · {s.designation}</option>)}
                </select>}
            </td>
            <td>
              {structuralReadonly ? (
                <span className="text-[10px]">{(it.team_members||[]).join(", ") || "—"}</span>
              ) : (
                <div className="flex gap-1">
                  <input className="nb-input text-xs flex-1" placeholder="Rolls e.g. 10A01,10A02"
                         defaultValue={(it.team_members||[]).map(m=>m.split("·")[0]).join(",")}
                         onBlur={e => patchRolls(it.item_id, e.target.value)}
                         data-testid={`event-item-rolls-${it.item_id}`}/>
                  <div className="text-[10px] text-slate-500 max-w-[100px] truncate self-center">
                    {(it.team_members||[]).map(m=>m.split("·")[1] || m).join(", ")}
                  </div>
                </div>
              )}
            </td>
            <td>{readonly ? (it.rehearsed?"Yes":"No") :
              <input type="checkbox" checked={!!it.rehearsed}
                     onChange={()=>patch(it.item_id, {rehearsed: !it.rehearsed})}
                     data-testid={`event-item-rehearsed-${it.item_id}`}/>}</td>
            <td>
              {structuralReadonly ? <span className="nb-badge">{it.approval_status}</span> :
                <select className="nb-select text-xs" value={it.approval_status||"pending"}
                        onChange={e=>patch(it.item_id, {approval_status:e.target.value})}
                        data-testid={`event-item-approval-${it.item_id}`}>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>}
            </td>
            <td className="mono font-bold">{it.performance_score ?? "—"}</td>
            <td>
              {readonly ? (it.rank ?? "—") :
                <input type="number" className="nb-input text-xs w-14" value={it.rank||""}
                       onChange={e=>patch(it.item_id, {rank: e.target.value ? parseInt(e.target.value):null})}
                       data-testid={`event-item-rank-${it.item_id}`}/>}
            </td>
            <td>
              {readonly ? (it.certificate_type || "—") :
                <select className="nb-select text-xs" value={it.certificate_type||""}
                        onChange={e=>patch(it.item_id, {certificate_type:e.target.value})}
                        data-testid={`event-item-cert-${it.item_id}`}>
                  <option value="">—</option>
                  {CERT_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                </select>}
              {it.certificate_type && <Award size={12} className="inline ml-1 text-yellow-600"/>}
            </td>
            <td>
              {!readonly && <>
                <ScoreEntry eventId={event.event_id} itemId={it.item_id} onDone={onDone}/>
                {!execMode && (
                  <button onClick={()=>del(it.item_id)} className="nb-btn nb-btn-danger px-1 py-0.5 text-[10px] ml-1"
                          data-testid={`event-item-del-${it.item_id}`}>×</button>
                )}
              </>}
            </td>
          </tr>
        ))}
        {items.length === 0 && <tr><td colSpan={11} className="text-center py-3 text-slate-500">No programs yet</td></tr>}
        {!readonly && !execMode && (
          <tr className="bg-yellow-50">
            <td className="mono">{items.length + 1}</td>
            <td><input className="nb-input text-xs w-full" placeholder="Program"
                       value={newItem.program_name}
                       onChange={e=>setNewItem({...newItem, program_name:e.target.value})}
                       data-testid="event-item-new-name"/></td>
            <td><div className="flex gap-1">
              <input type="time" className="nb-input text-xs w-20" value={newItem.time_from}
                     onChange={e=>setNewItem({...newItem, time_from:e.target.value})}/>
              <input type="time" className="nb-input text-xs w-20" value={newItem.time_to}
                     onChange={e=>setNewItem({...newItem, time_to:e.target.value})}/>
            </div></td>
            <td>
              <select className="nb-select text-xs w-full"
                      value={newItem.responsible_staff_id}
                      onChange={e=>setNewItem({...newItem, responsible_staff_id:e.target.value})}
                      data-testid="event-item-new-staff">
                <option value="">— Staff —</option>
                {(staff||[]).map(s => <option key={s.staff_id} value={s.staff_id}>{s.name}</option>)}
              </select>
            </td>
            <td><input className="nb-input text-xs w-full" placeholder="Rolls e.g. 10A01,10A02"
                       value={newItem.team_rolls}
                       onChange={e=>setNewItem({...newItem, team_rolls:e.target.value})}
                       data-testid="event-item-new-rolls"/></td>
            <td colSpan={5}></td>
            <td>
              <button onClick={add} className="nb-btn text-xs" data-testid="event-item-add">
                <Plus size={12}/>Add
              </button>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>;
}

function ScoreEntry({eventId, itemId, onDone}) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({judge_name:"", score:"", remarks:""});
  const save = async () => {
    if (!f.judge_name || !f.score) return toast.error("Judge + score required");
    try {
      await api.post(`/events/${eventId}/scores`,
        {item_id: itemId, judge_name: f.judge_name, score: parseFloat(f.score), remarks: f.remarks});
      toast.success("Score recorded");
      setF({judge_name:"", score:"", remarks:""}); setOpen(false); onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return <>
    <button onClick={()=>setOpen(true)} className="nb-btn nb-btn-secondary px-1 py-0.5 text-[10px]"
            data-testid={`event-score-open-${itemId}`}>Score</button>
    {open && (
      <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
           onClick={()=>setOpen(false)}>
        <div className="nb-card p-4 bg-white max-w-sm w-full space-y-2"
             onClick={e=>e.stopPropagation()}>
          <h4 className="display font-bold">Record Judge Score</h4>
          <input className="nb-input" placeholder="Judge name" value={f.judge_name}
                 onChange={e=>setF({...f, judge_name:e.target.value})}
                 data-testid="event-score-judge"/>
          <input type="number" step="0.1" min="0" max="100" className="nb-input mono"
                 placeholder="Score (0-100)" value={f.score}
                 onChange={e=>setF({...f, score:e.target.value})}
                 data-testid="event-score-value"/>
          <textarea className="nb-textarea" rows={2} placeholder="Remarks (optional)"
                    value={f.remarks}
                    onChange={e=>setF({...f, remarks:e.target.value})}/>
          <div className="flex gap-2">
            <button onClick={()=>setOpen(false)} className="nb-btn nb-btn-ghost flex-1 justify-center">Cancel</button>
            <button onClick={save} className="nb-btn flex-1 justify-center"
                    data-testid="event-score-save">Save Score</button>
          </div>
        </div>
      </div>
    )}
  </>;
}

function GreetingGenerator({eventId}) {
  const [open, setOpen] = useState(false);
  const [audience, setAudience] = useState("parent");
  const [text, setText] = useState("");
  const generate = async () => {
    try {
      const r = await api.post(`/events/${eventId}/greeting`, {audience});
      setText(r.data.text);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return <>
    <button onClick={()=>{setOpen(true); setText("");}}
            className="nb-btn nb-btn-secondary"
            data-testid={`event-greet-open-${eventId}`}>
      <MessageSquare size={14}/>Greeting Cards
    </button>
    {open && (
      <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
           onClick={()=>setOpen(false)}>
        <div className="nb-card p-6 bg-white max-w-lg w-full space-y-2"
             onClick={e=>e.stopPropagation()}>
          <h3 className="display text-xl font-bold">AI Greeting Generator</h3>
          <select className="nb-select" value={audience} onChange={e=>setAudience(e.target.value)}
                  data-testid="event-greet-audience">
            <option value="parent">Parents</option>
            <option value="teacher">Teachers</option>
            <option value="student">Students</option>
            <option value="chief_guest">Chief Guests</option>
          </select>
          <button onClick={generate} className="nb-btn w-full justify-center"
                  data-testid="event-greet-generate">Generate</button>
          {text && <>
            <textarea className="nb-textarea" rows={8} value={text} readOnly
                      data-testid="event-greet-text"/>
            <button onClick={()=>{navigator.clipboard.writeText(text); toast.success("Copied");}}
                    className="nb-btn nb-btn-ghost w-full justify-center">Copy to Clipboard</button>
          </>}
          <button onClick={()=>setOpen(false)} className="nb-btn nb-btn-ghost w-full justify-center">Close</button>
        </div>
      </div>
    )}
  </>;
}

function MediaCapture({event, onDone}) {
  const [uploading, setUploading] = useState(false);
  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: exceeds 10MB per-educator quota`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", file.type.startsWith("video") ? "video" : "photo");
      fd.append("caption", file.name);
      try {
        const r = await api.post(`/events/${event.event_id}/media`, fd, {
          headers: {"Content-Type": "multipart/form-data"},
        });
        const savings = r.data.compression_ratio
          ? ` (${Math.round((1 - r.data.compression_ratio) * 100)}% smaller)`
          : "";
        toast.success(`Uploaded ${file.name}${savings}`);
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    }
    setUploading(false);
    onDone && onDone();
  };
  return <div className="nb-card p-3 bg-slate-50" data-testid={`event-media-${event.event_id}`}>
    <div className="flex items-center gap-2 flex-wrap">
      <label className="nb-btn cursor-pointer" data-testid={`event-media-upload-${event.event_id}`}>
        <ImageIcon size={14}/>{uploading ? "Uploading..." : "Add Photos / Videos"}
        <input type="file" multiple accept="image/*,video/*" className="hidden"
               onChange={e=>upload(e.target.files)}/>
      </label>
      <label className="nb-btn nb-btn-secondary cursor-pointer">
        Camera
        <input type="file" accept="image/*" capture="environment" className="hidden"
               onChange={e=>upload(e.target.files)}/>
      </label>
      <span className="text-xs text-slate-500">Max 10 MB per educator · 25 educators per event · auto-compressed to WebP</span>
    </div>
    {(event.media||[]).length > 0 && (
      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
        {event.media.map(m => (
          <MediaThumb key={m.media_id} m={m}/>
        ))}
      </div>
    )}
  </div>;
}

function MediaThumb({m}) {
  const [blobUrl, setBlobUrl] = useState("");
  useEffect(() => {
    let revoke;
    if (m.storage_path) {
      api.get(`/files/serve/${m.storage_path}`, {responseType: "blob"})
        .then(r => { const u = URL.createObjectURL(r.data); setBlobUrl(u); revoke = u; })
        .catch(() => setBlobUrl(""));
    } else if (m.data_url) {
      setBlobUrl(m.data_url);
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [m.storage_path, m.data_url]);
  return <div className="border-2 border-black rounded overflow-hidden bg-white"
              data-testid={`event-media-item-${m.media_id}`}>
    {blobUrl && (m.kind === "photo" || (m.content_type||"").startsWith("image")) &&
      <img src={blobUrl} alt="" className="w-full h-24 object-cover" loading="lazy"/>}
    {blobUrl && (m.kind === "video" || (m.content_type||"").startsWith("video")) &&
      <video src={blobUrl} className="w-full h-24 object-cover" muted/>}
    <div className="p-1 text-[9px] truncate">{m.caption}</div>
    {m.syndicated && <div className="px-1 pb-1"><span className="nb-badge bg-[#86EFAC] text-[9px]">SYNDICATED</span></div>}
  </div>;
}
